import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

export function TestimonialsSection() {
  const { language } = useLanguage();

  const content = {
    fr: {
      eyebrow: "Preuves & retours terrain",
      title: "Une plateforme qui parle enfin le langage du pilotage local",
      subtitle:
        "Les retours clients sont les plus utiles quand ils décrivent une meilleure lecture, de meilleures priorités et un vrai gain de clarté au quotidien.",
      testimonials: [
        {
          name: "Camille R.",
          role: "Directrice de clinique locale",
          tag: "Réputation & organisation",
          quote:
            "Avant OuraTrust, nous jonglions entre les plateformes d'avis et des réponses trop lentes. Aujourd'hui, l'équipe voit enfin les priorités, traite les retours plus vite et garde une image beaucoup plus cohérente.",
        },
        {
          name: "Nicolas M.",
          role: "Gérant de restaurant",
          tag: "Visibilité locale",
          quote:
            "La vraie valeur n'est pas une promesse SEO spectaculaire. C'est le fait de comprendre où notre fiche est faible, comment on se compare localement et quoi améliorer en premier.",
        },
        {
          name: "Sarah T.",
          role: "Responsable réseau retail",
          tag: "Multi-sites premium",
          quote:
            "Nous avions besoin d'un outil sérieux, pas d'un gadget. OuraTrust nous aide à structurer réputation, visibilité locale et collecte dans une logique beaucoup plus propre pour plusieurs établissements.",
        },
      ],
    },
    en: {
      eyebrow: "Proof & field feedback",
      title: "A platform that finally matches real local operating needs",
      subtitle:
        "The most useful customer feedback is not about hype. It is about better clarity, better priorities and a stronger day-to-day operating view.",
      testimonials: [
        {
          name: "Camille R.",
          role: "Local clinic director",
          tag: "Reputation & operations",
          quote:
            "Before OuraTrust, we were juggling review platforms and slow replies. Now the team finally sees clear priorities, handles feedback faster and keeps a much more consistent public image.",
        },
        {
          name: "Nicolas M.",
          role: "Restaurant owner",
          tag: "Local visibility",
          quote:
            "The real value is not an exaggerated SEO promise. It is the ability to understand where our profile is weak, how we compare locally and what we should improve first.",
        },
        {
          name: "Sarah T.",
          role: "Retail network manager",
          tag: "Premium multi-site control",
          quote:
            "We needed a serious product, not a gadget. OuraTrust helps us structure reputation, local visibility and collection in a much cleaner operating model across locations.",
        },
      ],
    },
  };

  const page = content[language];

  return (
    <section className="border-y border-border bg-muted/30 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl text-center"
        >
          <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
            {page.eyebrow}
          </Badge>
          <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
            {page.title}
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.subtitle}</p>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-6 lg:grid-cols-3">
          {page.testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className="relative rounded-[2rem] border border-border bg-card p-8 shadow-sm"
            >
              <div className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Quote className="h-4 w-4 text-primary" />
              </div>
              <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-medium">
                {testimonial.tag}
              </Badge>
              <blockquote className="mt-6 text-base leading-8 text-muted-foreground">“{testimonial.quote}”</blockquote>
              <div className="mt-8 border-t border-border pt-5">
                <p className="font-medium text-foreground">{testimonial.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
